import { Box, Collapse } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import useGetOrCreateMidTermAdjustment, {
	handleChangeOfUsageOrMilageRating,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { MtaChangeType } from '@acorn-portal/shared/src/services/api';
import { updateMidTermAdjustmentDetails } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { isTelematics, isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import { checkFormConditions } from '@acorn-portal/shared/src/pages/mta/utils';

import {
	ANNUAL_MILEAGE,
	ANNUAL_MILEAGE_YD,
	BUSINESS_MILEAGE,
	COVER_LEVEL,
	TELEMATICS_ANNUAL_MILEAGE_HELP_TEXT,
	STREET_COVER_VEHICLE_USED_FOR,
} from '@acorn-portal/shared/src/utils/constants/modalText';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import AnnualMileageCalculator from '@acorn-portal/shared/src/components/calculators/AnnualMileageCalculator';
import ChangeOfUsageOrMileageSchema from '@acorn-portal/shared/src/utils/validation/ChangeOfUsageOrMileageSchema';
import { Option } from '@acorn-portal/shared/src/store/referenceData.slice';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IChangeUsageOrMileageForm {
	vehicleUse: string;
	// deliveryCompany: string;
	levelOfCover: string;
	annualMileage: string;
	businessMileage?: string;
	licencingAuthority?: string;
	operatingArea?: string;
}

const ChangeUsageOrMileage = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const [hasChanged] = useNoChangeWarning();
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const lists = useAppSelector((redux) => redux.referenceData);
	const mtaChanges = useAppSelector((redux) => redux.midTermAdjustment);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_USAGE_AND_MILEAGE,
		setAdjustmentLoading: setLoading,
	});

	useEffect(() => {
		if (!mtaChanges.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, []);

	const { discardLoading } = useDiscardMta();

	const GetBusinessMillageOptions = (annualMileage: string) => {
		return (
			lists.millageIncrementOptions.filter((option) => (option.value as number) <= parseInt(annualMileage, 10)) || []
		);
	};

	const initialValues = {
		vehicleUse: midTermAdjustment.vehicleDetails.vehicleUse,
		// deliveryCompany: midTermAdjustment.vehicleDetails.deliveryCompany ?? '', // TODO: check if this is missing in the API
		levelOfCover: midTermAdjustment.vehicleDetails.coverDefinitionCode ?? '',
		annualMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			midTermAdjustment.vehicleDetails.annualMileage,
		),
		businessMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			midTermAdjustment.vehicleDetails.businessMileage,
		),
	};

	const onSubmit = async (values: IChangeUsageOrMileageForm): Promise<void> => {
		const newMTAChanges = values;

		const activePolicyValues = {
			vehicleUse: activePolicyDetails.vehicleDetails.vehicleUse,
			// deliveryCompany: activePolicyDetails.vehicleDetails.deliveryCompany, // TODO: check if this is missing in the API
			levelOfCover: activePolicyDetails.vehicleDetails.coverDefinitionCode ?? '',
			annualMileage: getClosestMileage(
				lists.millageIncrementOptions as IOptions[],
				activePolicyDetails.vehicleDetails.annualMileage,
			),
			businessMileage: getClosestMileage(
				lists.millageIncrementOptions as IOptions[],
				activePolicyDetails.vehicleDetails.businessMileage,
			),
		};

		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, newMTAChanges),
			hasChanged(initialValues, newMTAChanges),
			mtaResponse,
			mtaChanges.changeOfUsage,
		);

		if (!continueToRate && !navigateToSummary) {
			return;
		}

		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		}

		setLoading(true);

		try {
			const result = await handleChangeOfUsageOrMilageRating({
				midTermAdjustment,
				values,
				product: fullPolicy.product,
			});

			dispatch(
				updateMidTermAdjustmentDetails({
					...midTermAdjustment,
					changeOfUsage: updateSection,
					vehicleDetails: {
						...midTermAdjustment.vehicleDetails,
						annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
						businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
						coverDefinitionCode: values.levelOfCover,
						vehicleUse: values.vehicleUse,
					},
				}),
			);

			if (!result) {
				throw new Error('No result returned from handleChangeOfUsageOrMilageRating');
			}

			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}

			dispatch(setMidTermAdjustmentRatingResponse(result));
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangeSummary);
		} catch (error) {
			console.error('Error during change of usage or mileage:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	// Remove all options which are not StreetCover related.
	const filteredVehicleUseOptions = useMemo(() => {
		return lists.vehicleUseOptions
			.filter((x) =>
				[
					'4', // social, domestic & pleasure
					'23', // Carriage of own goods for hire & reward
				].includes(x.value as string),
			)
			.map((x) => {
				return { ...x, isButton: true } as Option;
			}) as IOptions[];
	}, [lists.vehicleUseOptions]);

	const pageTitle = useMemo(() => {
		return `Change of ${fullPolicy.scheme === 'StreetCoverAnnual' ? 'Usage &' : ''} Mileage`;
	}, [fullPolicy]);

	return (
		<JourneyCardLayout pageTitle={pageTitle} loading={loading || discardLoading}>
			<Formik<IChangeUsageOrMileageForm>
				key={JSON.stringify(midTermAdjustment.transactionId)}
				validationSchema={ChangeOfUsageOrMileageSchema(fullPolicy)}
				initialValues={initialValues}
				onSubmit={onSubmit}
				onReset={() => navigate(-1)}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						{fullPolicy.scheme === 'StreetCoverAnnual' && (
							<ToggleButtonFormField
								label='What will you be using the vehicle for?'
								name='vehicleUse'
								id='vehicleUse'
								value={formik.values.vehicleUse}
								onChange={(value) => {
									formik.setFieldValue('vehicleUse', value);
									if (!isUsedForBusiness(value)) {
										formik.setFieldValue('businessMileage', undefined);
									}
								}}
								options={filteredVehicleUseOptions}
								error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
								helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
								modal={{
									title: STREET_COVER_VEHICLE_USED_FOR.header,
									description: STREET_COVER_VEHICLE_USED_FOR.text,
								}}
							/>
						)}

						<Collapse in={formik.values.vehicleUse === '21'}>
							<SelectFormField
								label='Which company do you primarily deliver for?'
								name='deliveryCompany'
								id='deliveryCompany'
								value={''} // formik.values.deliveryCompany
								onChange={(value) => {
									formik.setFieldValue('deliveryCompany', value);
								}}
								options={lists.deliveryOperators as IOptions[]}
								modal={{
									title: ANNUAL_MILEAGE.header,
									description: !isTelematics(fullPolicy.product) ? ANNUAL_MILEAGE.text : ANNUAL_MILEAGE_YD.text,
								}}
								error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
								helperText={formik.touched.annualMileage && formik.errors.annualMileage}
							/>
						</Collapse>

						<SelectFormField
							label='Annual mileage'
							name='annualMileage'
							id='annualMileage'
							value={formik.values.annualMileage}
							onChange={(value) => {
								formik.setFieldValue('annualMileage', value);
							}}
							options={lists.millageIncrementOptions as IOptions[]}
							modal={{
								title: ANNUAL_MILEAGE.header,
								description: !isTelematics(fullPolicy.product) ? ANNUAL_MILEAGE.text : ANNUAL_MILEAGE_YD.text,
							}}
							error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
							helperText={formik.touched.annualMileage && formik.errors.annualMileage}
						/>
						{isTelematics(fullPolicy.product) && (
							<Box
								sx={{
									position: 'relative',
									top: '-22px',
								}}
							>
								<AnnualMileageCalculator
									miles={parseInt(formik.values.annualMileage, 10)}
									id='annualMileageCalculator'
									helperText={TELEMATICS_ANNUAL_MILEAGE_HELP_TEXT}
								/>
							</Box>
						)}
						<Collapse in={isUsedForBusiness(formik.values.vehicleUse)}>
							<Box>
								<SelectFormField
									label='Business mileage'
									name='businessMileage'
									id='businessMileage'
									value={formik.values.businessMileage}
									onChange={(value) => {
										formik.setFieldValue('businessMileage', value);
									}}
									options={GetBusinessMillageOptions(formik.values.annualMileage) as IOptions[]}
									modal={{
										title: BUSINESS_MILEAGE.header,
										description: BUSINESS_MILEAGE.text,
									}}
									error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
									helperText={formik.touched.businessMileage && formik.errors.businessMileage}
								/>
							</Box>
						</Collapse>

						{activePolicyDetails.vehicleDetails.coverDefinitionCode !== 'COMP' ? null : (
							<SelectFormField
								label='What level of cover do you require?'
								name='levelOfCover'
								id='levelOfCover'
								value={formik.values.levelOfCover}
								onChange={(value) => formik.setFieldValue('levelOfCover', value)}
								options={lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[]}
								error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
								helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
								modal={{
									title: COVER_LEVEL(fullPolicy.startDate, fullPolicy.product).header,
									description: COVER_LEVEL(fullPolicy.startDate, fullPolicy.product).text,
								}}
							/>
						)}
						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								toggleModal(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={() => {
								formik.resetForm();
							}}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeUsageOrMileage;
